import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { t } from 'app/core/i18n/i18n';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { BusinessPartnerService } from 'app/modules/components/business-partners/business-partner/business-partner.service';
import { BusinessPartnerSubRoutesModalComponent } from 'app/modules/components/routing/sub-routes/sub-route-periodicity/business-partner-sub-routes/business-partner-sub-routes.component';
import { SmartGridComponent } from 'app/shared/components/smart-grid/components/smart-grid/smart-grid.component';
import { ColDef } from 'app/shared/components/smart-grid/core/interfaces/col-def.interface';
import { GridConfig } from 'app/shared/components/smart-grid/core/interfaces/grid-config.interface';
import { BusinessPartnerBySubRoute } from '../../../interfaces/busines-partner-by-sub-route.interface';
import { AddressCellRendererComponent } from '../../assigned-partners-list/cell-renderers/address-cell-renderer/address-cell-renderer.component';
import { ClientCellRendererComponent } from '../../assigned-partners-list/cell-renderers/client-cell-renderer/client-cell-renderer.component';
import { ContactCellRendererComponent } from '../../assigned-partners-list/cell-renderers/contact-cell-renderer/contact-cell-renderer.component';
import { DeleteCellRendererComponent } from '../../assigned-partners-list/cell-renderers/delete-cell-renderer/delete-cell-renderer.component';
import { GpsModeCellRendererComponent } from '../../assigned-partners-list/cell-renderers/gps-mode-cell-renderer/gps-mode-cell-renderer.component';
import { FrequencyCellRendererComponent } from '../../assigned-partners-list/cell-renderers/frequency-cell-renderer/frequency-cell-renderer.component';
import { RepinCellRendererComponent } from '../../assigned-partners-list/cell-renderers/repin-cell-renderer/repin-cell-renderer.component';
import { SubRoutePartnersLayoutService } from '../../sub-route-partners-layout.service';
import { AssignedPartnersSubRouteFilters } from '../../sub-route-partners.types';
import { BPGroupedItem, BPGroupingCriteria } from '../../types/bp-grouping.types';
import { AssignedPartnersPanelService } from '../assigned-partners-panel.service';
import { AssignedPartnersGroupedService } from './assigned-partners-grouped.service';

/** Data for an expanded row */
interface ExpandedRowData {
  loading: boolean;
  partners: BusinessPartnerBySubRoute[];
  totalItems: number;
  currentPage: number;
  pageSize: number;
}

@Component({
  selector: 'app-assigned-partners-grouped',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    SmartGridComponent,
    TranslatePipe,
  ],
  providers: [AssignedPartnersGroupedService],
  templateUrl: './assigned-partners-grouped.component.html',
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignedPartnersGroupedComponent {
  private groupedService = inject(AssignedPartnersGroupedService);
  private panelService = inject(AssignedPartnersPanelService);
  private layoutService = inject(SubRoutePartnersLayoutService);
  private businessPartnerService = inject(BusinessPartnerService);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private handlerMessage = inject(HandlerMessageService);

  constructor() {
    // Refresh grouped data when partners are assigned from unassigned sidebar
    this.layoutService.refreshAssignedPartners$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.groupedService.reload$.next();
    });
  }

  // === SIGNALS FROM SERVICE ===
  readonly data = this.groupedService.data;
  readonly loading = this.groupedService.loading;
  readonly totalItems = this.groupedService.totalItems;
  readonly pagination = this.groupedService.pagination;
  readonly groupingCriteria = this.groupedService.groupingCriteria;

  // === TABLE CONFIG ===
  readonly displayedColumns = ['expand', 'name', 'code', 'count'];

  // === EXPANDED ROWS TRACKING ===
  readonly expandedRows = signal<Set<string>>(new Set());
  readonly expandedRowsData = signal<Map<string, ExpandedRowData>>(new Map());

  // === EXPANDED ROW SMARTGRID CONFIG ===
  readonly expandedGridConfig: GridConfig<BusinessPartnerBySubRoute> = {
    pagination: {
      enabled: false,
    },
    sorting: {
      enabled: false,
    },
    styling: {
      cellPaddingY: '1px',
      fontSize: '12px',
      // NOT the standard list height. This grid is a panel inside another screen, so a
      // viewport-relative floor would push its own paginator, and the page around it, out of
      // view. Height stays as the panel had it.
      maxHeight: '400px',
    },
    responsive: {
      enabled: false,
    },
    columnVisibility: {
      enabled: false,
    },
  };

  // === EXPANDED ROW COLUMN DEFINITIONS ===
  readonly expandedColDefs: ColDef<BusinessPartnerBySubRoute>[] = [
    {
      colId: 'client',
      headerName: 'routing.SUB_ROUTE_PARTNERS.GROUPED.COL_CLIENT',
      cellRenderer: ClientCellRendererComponent,
      cellRendererParams: {
        onOpenSubRoutes: (bp: BusinessPartnerBySubRoute) => this.openSubRoutesModal(bp),
        getCompanyId: () => this.panelService.subRouteData()?.company?._id?.toString() ?? null,
      },
      minWidth: 220,
      flex: 1,
    },
    {
      colId: 'contact',
      headerName: 'routing.SUB_ROUTE_PARTNERS.GROUPED.COL_CONTACT',
      cellRenderer: ContactCellRendererComponent,
      minWidth: 180,
      flex: 1,
    },
    {
      colId: 'address',
      headerName: 'routing.SUB_ROUTE_PARTNERS.GROUPED.COL_ADDRESS',
      cellRenderer: AddressCellRendererComponent,
      minWidth: 200,
      flex: 1,
    },
    {
      colId: 'useGpsMode',
      headerName: 'routing.SUB_ROUTE_PARTNERS.GROUPED.COL_GPS_MODE',
      cellRenderer: GpsModeCellRendererComponent,
      cellRendererParams: {
        onRefresh: () => this.reloadExpandedData(),
      },
      minWidth: 120,
      flex: 0.5,
    },
    {
      colId: 'repin',
      headerName: 'routing.SUB_ROUTE_PARTNERS.GROUPED.COL_REPIN',
      cellRenderer: RepinCellRendererComponent,
      cellRendererParams: {
        onGeoUpdateSuccess: () => this.reloadExpandedData(),
      },
      minWidth: 100,
    },
    {
      colId: 'frequency',
      headerName: 'routing.SUB_ROUTE_PARTNERS.GROUPED.COL_FREQUENCY',
      cellRenderer: FrequencyCellRendererComponent,
      cellRendererParams: {
        getSubRouteId: () => this.panelService.subRouteIdForGroupView(),
        getSubRouteData: () => this.panelService.subRouteData(),
        onRefresh: () => this.reloadExpandedData(),
      },
      minWidth: 140,
      flex: 1,
    },
    {
      colId: 'delete',
      headerName: '',
      cellRenderer: DeleteCellRendererComponent,
      cellRendererParams: {
        getSubRouteId: () => this.panelService.subRouteIdForGroupView(),
        onDeleteSuccess: () => this.reloadExpandedData(),
      },
      minWidth: 50,
      maxWidth: 50,
    },
  ];

  /**
   * Toggle row expansion state and load data if expanding.
   */
  onRowExpand(item: BPGroupedItem): void {
    this.expandedRows.update((set) => {
      const newSet = new Set(set);
      if (newSet.has(item._id)) {
        newSet.delete(item._id);
      } else {
        newSet.add(item._id);
        this.loadExpandedPartners(item);
      }
      return newSet;
    });
  }

  /**
   * Load business partners for an expanded group.
   */
  private loadExpandedPartners(item: BPGroupedItem, page = 1, pageSize = 10): void {
    const subRouteId = this.panelService.subRouteIdForGroupView();
    if (!subRouteId) return;

    // Set loading state
    this.expandedRowsData.update((map) => {
      const newMap = new Map(map);
      const existing = newMap.get(item._id);
      newMap.set(item._id, {
        loading: true,
        partners: existing?.partners ?? [],
        totalItems: existing?.totalItems ?? 0,
        currentPage: page,
        pageSize,
      });
      return newMap;
    });

    // Build filters based on grouping criteria
    const filters = this.buildFiltersForGroup(item);

    this.businessPartnerService
      .findAssignedToSubRouteBusinessPartnerWithCount(
        filters,
        { currentPage: page, limitPerPage: pageSize },
        subRouteId,
        this.panelService.subRouteData()?.company?._id?.toString() ?? null
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.expandedRowsData.update((map) => {
            const newMap = new Map(map);
            newMap.set(item._id, {
              loading: false,
              partners: response.data?.data ?? [],
              totalItems: response.data?.pageInfo?.total ?? 0,
              currentPage: page,
              pageSize,
            });
            return newMap;
          });
        },
        error: (err) => {
          this.expandedRowsData.update((map) => {
            const newMap = new Map(map);
            newMap.set(item._id, {
              loading: false,
              partners: [],
              totalItems: 0,
              currentPage: page,
              pageSize,
            });
            return newMap;
          });
          this.handlerMessage.error({
            message: err?.error?.message,
            alternativeMessage: t('routing.SUB_ROUTE_PARTNERS.ERRORS.LOAD_FAILED'),
          });
        },
      });
  }

  /**
   * Build filters based on the current grouping criteria and item.
   */
  private buildFiltersForGroup(item: BPGroupedItem): AssignedPartnersSubRouteFilters {
    const criteria = this.groupingCriteria();
    const filters: AssignedPartnersSubRouteFilters = {};

    switch (criteria) {
      case BPGroupingCriteria.BUSINESS_PARTNER_GROUP:
        filters.businessPartnerGroupId = item.id;
        break;
      case BPGroupingCriteria.BUSINESS_PARTNER_SUB_GROUP:
        filters.businessPartnerSubGroupId = item.id;
        break;
      case BPGroupingCriteria.ROUTE:
        filters.routeId = item.id;
        break;
      case BPGroupingCriteria.ROUTE_AREA:
        filters.routeAreaId = item.id;
        break;
      case BPGroupingCriteria.SALES_OFFICE:
        filters.salesOfficeId = item.id;
        break;
      case BPGroupingCriteria.LOCATION_ONE:
        filters.locationOneCode = item.code === 'S/N' ? null : item.code;
        break;
      case BPGroupingCriteria.LOCATION_TWO:
        filters.locationTwoCode = item.code === 'S/N' ? null : item.code;
        break;
      case BPGroupingCriteria.LOCATION_THREE:
        filters.locationThreeCode = item.code === 'S/N' ? null : item.code;
        break;
      case BPGroupingCriteria.LOCATION_FOUR:
        filters.locationFourCode = item.code === 'S/N' ? null : item.code;
        break;
    }

    return filters;
  }

  /**
   * Check if a row is currently expanded.
   */
  isExpanded(item: BPGroupedItem): boolean {
    return this.expandedRows().has(item._id);
  }

  /**
   * Get expanded row data for an item.
   */
  getExpandedData(itemId: string): ExpandedRowData | undefined {
    return this.expandedRowsData().get(itemId);
  }

  /**
   * Handle pagination for expanded row.
   */
  onExpandedPageChange(item: BPGroupedItem, event: PageEvent): void {
    this.loadExpandedPartners(item, event.pageIndex + 1, event.pageSize);
  }

  /**
   * Handle pagination changes for the main table.
   */
  onPageChange(event: PageEvent): void {
    this.groupedService.updatePagination$.next({
      currentPage: event.pageIndex + 1,
      limitPerPage: event.pageSize,
    });
  }

  /**
   * Open the sub-routes modal for a business partner.
   */
  openSubRoutesModal(businessPartner: BusinessPartnerBySubRoute): void {
    this.dialog.open(BusinessPartnerSubRoutesModalComponent, {
      data: {
        businessPartner: businessPartner,
        subRoutes: businessPartner.subRoutes,
      },
    });
  }

  /**
   * Reload data for all expanded rows.
   */
  private reloadExpandedData(): void {
    this.expandedRows().forEach((itemId) => {
      const item = this.data().find((d) => d._id === itemId);
      if (item) {
        const expandedData = this.expandedRowsData().get(itemId);
        if (expandedData) {
          this.loadExpandedPartners(item, expandedData.currentPage, expandedData.pageSize);
        }
      }
    });
  }
}
